/**
 * RBAC Store — role-based access control for Platform Central tiles.
 *
 * Roles (highest → lowest privilege):
 *   admin        – full platform access, including Admin Console
 *   contributor  – deploy & configure, no admin panel
 *   viewer       – read-only access to all visible tiles
 *   reader       – read-only access to a restricted set of tiles
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type RBACRole = 'admin' | 'contributor' | 'viewer' | 'reader'

export const ROLE_LABELS: Record<RBACRole, string> = {
  admin:       'Admin',
  contributor: 'Contributor',
  viewer:      'Viewer',
  reader:      'Reader',
}

export const ROLE_DESCRIPTIONS: Record<RBACRole, string> = {
  admin:       'Full platform access including Admin Console and all configuration panels',
  contributor: 'Can deploy, configure, and manage resources but cannot access Admin Console',
  viewer:      'Read-only access to all tiles, dashboards, and logs',
  reader:      'Restricted read-only access to status and monitoring tiles only',
}

/** Hierarchy: higher index = higher privilege */
const ROLE_ORDER: RBACRole[] = ['reader', 'viewer', 'contributor', 'admin']

export function roleRank(role: RBACRole): number {
  return ROLE_ORDER.indexOf(role)
}

export function hasMinRole(userRole: RBACRole, required: RBACRole): boolean {
  return roleRank(userRole) >= roleRank(required)
}

export interface TilePermission {
  tileId: string
  /** Minimum role required to VIEW this tile */
  minViewRole: RBACRole
  /** Minimum role required to perform ACTIONS (deploy, configure, etc.) */
  minActionRole: RBACRole
}

/** Default permissions per tile */
export const DEFAULT_TILE_PERMISSIONS: TilePermission[] = [
  // ── Deployments ──────────────────────────────────────────────────────────
  { tileId: 'new-env',        minViewRole: 'viewer',      minActionRole: 'contributor' },
  { tileId: 'mcp-deploy',     minViewRole: 'viewer',      minActionRole: 'contributor' },
  { tileId: 'agent-deploy',   minViewRole: 'viewer',      minActionRole: 'contributor' },
  { tileId: 'app-in-aks',     minViewRole: 'viewer',      minActionRole: 'contributor' },
  // ── Operations ───────────────────────────────────────────────────────────
  { tileId: 'release-mgmt',   minViewRole: 'viewer',      minActionRole: 'contributor' },
  { tileId: 'patch-mgmt',     minViewRole: 'viewer',      minActionRole: 'contributor' },
  // ── Infrastructure ───────────────────────────────────────────────────────
  { tileId: 'haproxy',        minViewRole: 'viewer',      minActionRole: 'admin'       },
  { tileId: 'cloudflare',     minViewRole: 'viewer',      minActionRole: 'admin'       },
  { tileId: 'certificates',   minViewRole: 'viewer',      minActionRole: 'contributor' },
  // ── Observability ─────────────────────────────────────────────────────────
  { tileId: 'status-central', minViewRole: 'reader',      minActionRole: 'viewer'      },
  { tileId: 'monitoring',     minViewRole: 'reader',      minActionRole: 'viewer'      },
  // ── CMDB ─────────────────────────────────────────────────────────────────
  { tileId: 'cmdb',           minViewRole: 'reader',      minActionRole: 'contributor' },
  // ── Financial ─────────────────────────────────────────────────────────────
  { tileId: 'cost-optimize',  minViewRole: 'viewer',      minActionRole: 'contributor' },
  { tileId: 'cost-reports',   minViewRole: 'viewer',      minActionRole: 'viewer'      },
  // ── Support ───────────────────────────────────────────────────────────────
  { tileId: 'service-desk',   minViewRole: 'reader',      minActionRole: 'viewer'      },
]

interface RBACState {
  currentRole: RBACRole
  tilePermissions: TilePermission[]

  setRole: (role: RBACRole) => void
  setTilePermission: (tileId: string, perm: Partial<TilePermission>) => void
  resetPermissions: () => void

  /** Can the current user see this tile at all? */
  canView: (tileId: string) => boolean
  /** Can the current user perform actions (deploy, configure) on this tile? */
  canAct: (tileId: string) => boolean
  /** Is the current user at least Admin? */
  isAdmin: () => boolean
}

export const useRBACStore = create<RBACState>()(
  persist(
    (set, get) => ({
      currentRole: 'admin',
      tilePermissions: DEFAULT_TILE_PERMISSIONS,

      setRole: (role) => set({ currentRole: role }),

      setTilePermission: (tileId, patch) =>
        set(state => ({
          tilePermissions: state.tilePermissions.map(p =>
            p.tileId === tileId ? { ...p, ...patch } : p
          ),
        })),

      resetPermissions: () => set({ tilePermissions: DEFAULT_TILE_PERMISSIONS }),

      canView: (tileId) => {
        const { currentRole, tilePermissions } = get()
        if (currentRole === 'admin') return true
        const perm = tilePermissions.find(p => p.tileId === tileId)
        if (!perm) return true // default allow
        return hasMinRole(currentRole, perm.minViewRole)
      },

      canAct: (tileId) => {
        const { currentRole, tilePermissions } = get()
        if (currentRole === 'admin') return true
        const perm = tilePermissions.find(p => p.tileId === tileId)
        if (!perm) return currentRole === 'contributor'
        return hasMinRole(currentRole, perm.minActionRole)
      },

      isAdmin: () => get().currentRole === 'admin',
    }),
    { name: 'dc-rbac' }
  )
)
